'use client';

import { useMemo, useState } from 'react';
import { collection, limit, orderBy, query } from 'firebase/firestore';
import { Check, Loader2, Play, Plus, Square, Trash2, Trophy, Vote, X } from 'lucide-react';
import { db } from '@/firebase/config';
import { useCollection } from '@/firebase/firestore/use-collection';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { crearVotacion, eliminarVotacion, empezarVotacion, finalizarVotacion } from '@/lib/actions';
import type { JartinetHermandad, UserProfile, Votacion } from '@/lib/types';

function toDateSafe(v: any): Date | null {
  if (!v) return null;
  if (typeof v === 'object' && typeof v.toDate === 'function') return v.toDate();
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

function fmtFecha(v: any): string {
  const d = toDateSafe(v);
  return d ? d.toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
}

const ESTADOS: Record<Votacion['estado'], { label: string; cls: string }> = {
  borrador: { label: 'Borrador', cls: 'border-white/15 bg-white/5 text-muted-foreground' },
  abierta: { label: 'Abierta', cls: 'border-emerald-400/40 bg-emerald-500/15 text-emerald-300' },
  cerrada: { label: 'Finalizada', cls: 'border-rose-400/40 bg-rose-500/15 text-rose-300' },
};

/**
 * Pestaña «Votaciones» del panel de la hermandad (solo HM/admin). Sigue en vivo
 * la subcolección `votaciones`: recuento, quién ha votado cada opción y quién
 * falta. Crear, empezar, finalizar y borrar van por la CF `votaciones`.
 */
export function VotacionesSection({ hermandad, hermanos }: { hermandad: JartinetHermandad; hermanos: UserProfile[] }) {
  const votacionesQuery = useMemo(
    () => query(collection(db, 'jartinet_hermandades', hermandad.id, 'votaciones'), orderBy('createdAt', 'desc'), limit(50)),
    [hermandad.id],
  );
  const { data, isLoading } = useCollection<Votacion>(votacionesQuery);
  const votaciones = (data || []) as Votacion[];
  const nombres = useMemo(() => new Map(hermanos.map((h) => [h.id, h.nickname || h.id])), [hermanos]);
  const [nuevaOpen, setNuevaOpen] = useState(false);
  const color = hermandad.colorPrimario || '#fbd641';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 text-lg font-semibold"><Vote className="h-5 w-5" /> Votaciones</h3>
        <Button size="sm" onClick={() => setNuevaOpen(true)} className="gap-1.5"><Plus className="h-4 w-4" /> Nueva votación</Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Al empezar una votación le llega una carta al buzón a cada hermano para votar. Aquí ves en directo cómo va y
        qué ha votado cada uno, y la finalizas cuando quieras.
      </p>

      {isLoading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : votaciones.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed py-10 text-center">
          <Vote className="mx-auto mb-2 h-8 w-8 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Todavía no hay votaciones.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {votaciones.map((v) => (
            <VotacionCard key={v.id} v={v} hermandadId={hermandad.id} color={color} nombres={nombres} totalHermanos={hermanos.length} />
          ))}
        </div>
      )}

      <NuevaVotacionDialog open={nuevaOpen} onOpenChange={setNuevaOpen} hermandadId={hermandad.id} totalHermanos={hermanos.length} />
    </div>
  );
}

function NuevaVotacionDialog({ open, onOpenChange, hermandadId, totalHermanos }: {
  open: boolean; onOpenChange: (o: boolean) => void; hermandadId: string; totalHermanos: number;
}) {
  const { toast } = useToast();
  const [titulo, setTitulo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [opciones, setOpciones] = useState<string[]>(['', '']);
  const [enviando, setEnviando] = useState<'borrador' | 'empezar' | null>(null);

  const limpias = opciones.map((o) => o.trim()).filter(Boolean);
  const valida = titulo.trim().length > 0 && new Set(limpias).size >= 2;

  const reset = () => { setTitulo(''); setDescripcion(''); setOpciones(['', '']); };

  const guardar = async (empezar: boolean) => {
    setEnviando(empezar ? 'empezar' : 'borrador');
    const r = await crearVotacion(hermandadId, titulo.trim(), descripcion.trim(), limpias);
    const id = r.data?.id as string | undefined;
    if (!r.success || !id) {
      toast({ title: 'Error', description: r.message || 'No se pudo crear.', variant: 'destructive' });
      setEnviando(null);
      return;
    }
    if (empezar) {
      const e = await empezarVotacion(hermandadId, id);
      if (!e.success) {
        toast({ title: 'Creada, pero no empezada', description: e.message, variant: 'destructive' });
      } else {
        toast({ title: 'Votación empezada', description: `Enviada al buzón de ${e.data?.enviadas ?? 0} hermanos.` });
      }
    } else {
      toast({ title: 'Borrador guardado', description: 'Empiézala cuando quieras desde la lista.' });
    }
    setEnviando(null);
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90dvh] flex-col p-0 sm:max-w-lg">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle className="flex items-center gap-2"><Vote className="h-5 w-5" /> Nueva votación</DialogTitle>
          <DialogDescription>Título y opciones. Al empezarla, le llega a los {totalHermanos} hermanos por el buzón.</DialogDescription>
        </DialogHeader>
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 pb-6">
          <div className="space-y-1.5">
            <Label htmlFor="vot-titulo">Título</Label>
            <Input id="vot-titulo" value={titulo} maxLength={150} onChange={(e) => setTitulo(e.target.value)} placeholder="Ej.: ¿Qué marcha abre la salida?" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="vot-desc">Explicación (opcional)</Label>
            <Textarea id="vot-desc" value={descripcion} maxLength={1000} rows={3} onChange={(e) => setDescripcion(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Opciones</Label>
            {opciones.map((o, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="w-5 shrink-0 text-right text-xs text-muted-foreground">{i + 1}.</span>
                <Input value={o} maxLength={100} placeholder={`Opción ${i + 1}`}
                  onChange={(e) => setOpciones((prev) => prev.map((x, j) => (j === i ? e.target.value : x)))} />
                <Button type="button" size="icon" variant="ghost" className="h-9 w-9 shrink-0" disabled={opciones.length <= 2}
                  onClick={() => setOpciones((prev) => prev.filter((_, j) => j !== i))}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
            {opciones.length < 12 && (
              <Button type="button" size="sm" variant="outline" className="gap-1.5" onClick={() => setOpciones((prev) => [...prev, ''])}>
                <Plus className="h-4 w-4" /> Añadir opción
              </Button>
            )}
          </div>
          <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
            <Button variant="outline" disabled={!valida || !!enviando} onClick={() => guardar(false)}>
              {enviando === 'borrador' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Guardar borrador
            </Button>
            <Button disabled={!valida || !!enviando} onClick={() => guardar(true)} className="gap-1.5">
              {enviando === 'empezar' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} Empezar votación
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function VotacionCard({ v, hermandadId, color, nombres, totalHermanos }: {
  v: Votacion; hermandadId: string; color: string; nombres: Map<string, string>; totalHermanos: number;
}) {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);

  const votos = v.votos || {};
  const censo = v.censo || [];
  const total = Object.keys(votos).length;
  const porOpcion = (v.opciones || []).map((o) => ({
    ...o,
    votantes: Object.entries(votos)
      .filter(([, x]) => x.opcionId === o.id)
      .map(([uid, x]) => x.nickname || nombres.get(uid) || uid)
      .sort((a, b) => a.localeCompare(b, 'es')),
  }));
  const max = Math.max(0, ...porOpcion.map((o) => o.votantes.length));
  const sinVotar = censo.filter((uid) => !votos[uid]).map((uid) => nombres.get(uid) || 'Hermano (ya no está)');
  const estado = ESTADOS[v.estado] || ESTADOS.borrador;

  const accion = async (fn: () => Promise<{ success: boolean; message?: string }>, ok: string) => {
    setBusy(true);
    const r = await fn();
    toast(r.success ? { title: ok } : { title: 'Error', description: r.message, variant: 'destructive' });
    setBusy(false);
  };

  return (
    <div className="relative space-y-3 overflow-hidden rounded-2xl border border-white/8 bg-muted/30 p-4">
      <div className="absolute inset-x-0 top-0 h-[3px]" style={{ backgroundColor: color }} />

      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-semibold leading-snug">{v.titulo}</p>
          <p className="mt-0.5 text-[0.7rem] text-muted-foreground">
            {v.estado === 'borrador' && `Creada ${fmtFecha(v.createdAt)}`}
            {v.estado === 'abierta' && `Abierta desde ${fmtFecha(v.startedAt)}`}
            {v.estado === 'cerrada' && `Finalizada ${fmtFecha(v.closedAt)}`}
          </p>
        </div>
        <span className={cn('flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[0.7rem] font-semibold', estado.cls)}>
          {v.estado === 'abierta' && <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" />}
          {estado.label}
        </span>
      </div>

      {v.descripcion && <p className="whitespace-pre-line text-sm text-muted-foreground">{v.descripcion}</p>}

      {v.estado !== 'borrador' && (
        <p className="text-sm">
          <strong>{total}</strong> de {censo.length} han votado
          {censo.length > 0 && <span className="text-muted-foreground"> · {Math.round((total / censo.length) * 100)} %</span>}
        </p>
      )}

      <div className="space-y-2">
        {porOpcion.map((o) => {
          const n = o.votantes.length;
          const pct = total ? Math.round((n / total) * 100) : 0;
          const gana = v.estado === 'cerrada' && max > 0 && n === max;
          return (
            <div key={o.id} className={cn('rounded-xl border p-2.5', gana ? 'border-amber-400/50 bg-amber-400/10' : 'border-white/5 bg-background/40')}>
              <div className="flex items-center justify-between gap-2 text-sm">
                <span className="flex min-w-0 items-center gap-1.5 font-medium">
                  {gana && <Trophy className="h-4 w-4 shrink-0 text-amber-400" />}
                  <span className="truncate">{o.texto}</span>
                </span>
                {v.estado !== 'borrador' && <span className="shrink-0 font-bold tabular-nums">{n} <span className="font-normal text-muted-foreground">· {pct} %</span></span>}
              </div>
              {v.estado !== 'borrador' && (
                <>
                  <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-white/5">
                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
                  </div>
                  {n > 0 && <p className="mt-1.5 text-xs text-muted-foreground">{o.votantes.join(', ')}</p>}
                </>
              )}
            </div>
          );
        })}
      </div>

      {v.estado !== 'borrador' && sinVotar.length > 0 && (
        <p className="text-xs text-muted-foreground">
          <span className="font-semibold text-foreground/80">Sin votar ({sinVotar.length}):</span> {sinVotar.join(', ')}
        </p>
      )}

      <div className="flex flex-wrap gap-2 pt-1">
        {v.estado === 'borrador' && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button size="sm" disabled={busy} className="gap-1.5"><Play className="h-4 w-4" /> Empezar votación</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>¿Empezar «{v.titulo}»?</AlertDialogTitle>
                <AlertDialogDescription>Se enviará una carta al buzón de los {totalHermanos} hermanos para que voten.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={() => accion(() => empezarVotacion(hermandadId, v.id), 'Votación empezada')}>Empezar</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
        {v.estado === 'abierta' && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button size="sm" variant="destructive" disabled={busy} className="gap-1.5"><Square className="h-4 w-4" /> Finalizar votación</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>¿Finalizar «{v.titulo}»?</AlertDialogTitle>
                <AlertDialogDescription>
                  Han votado {total} de {censo.length}. Una vez finalizada ya no se podrá votar.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={() => accion(() => finalizarVotacion(hermandadId, v.id), 'Votación finalizada')} className="bg-destructive hover:bg-destructive/80">Finalizar</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
        {v.estado !== 'abierta' && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button size="sm" variant="ghost" disabled={busy} className="gap-1.5 text-destructive/80 hover:text-destructive"><Trash2 className="h-4 w-4" /> Borrar</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>¿Borrar «{v.titulo}»?</AlertDialogTitle>
                <AlertDialogDescription>Se borra la votación con sus resultados. Las cartas ya enviadas se quedan en los buzones.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={() => accion(() => eliminarVotacion(hermandadId, v.id), 'Votación borrada')} className="bg-destructive hover:bg-destructive/80">Borrar</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
        {v.estado === 'cerrada' && total > 0 && (
          <span className="flex items-center gap-1 text-xs text-muted-foreground"><Check className="h-3.5 w-3.5" /> Resultado final</span>
        )}
      </div>
    </div>
  );
}
